import React, { useState, useEffect, useRef } from "react";
import Close from "../../../assets/images/cancelBlack.svg";
import { useSelector } from "react-redux";
import useApi from "../../../helpers/apiHelper/requestHelper";
import "./AlertsDropdown.css";

const isSpecialAlert = (item) => Boolean(item?.isSpecial);

const AlertsDropdown = () => {
  const user = useSelector((e) => e?.user?.user);
  const api = useApi();
  const [isOpen, setIsOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [isClearing, setIsClearing] = useState(false);
  const dropdownRef = useRef(null);

  // All hooks must be declared before any conditional return
  const isMember = user?.role === "member";

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  const toggleDropdown = () => setIsOpen((prev) => !prev);

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Lock body scroll on mobile when panel is open
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("mobile-scroll-locked");
    } else {
      document.body.classList.remove("mobile-scroll-locked");
    }
    return () => document.body.classList.remove("mobile-scroll-locked");
  }, [isOpen]);

  // Fetch special alerts from the same notifications API
  useEffect(() => {
    if (!isMember) return;

    const fetchAlerts = async (e) => {
      const detail = e?.detail;

      // Optimistically add local special alerts that aren't in the API yet
      if (detail?.isSpecial && (detail?.title || detail?.message || detail?.body)) {
        const localItem = {
          _id: detail._id || `local-alert-${Date.now()}`,
          title: detail.title || "Alert",
          message: detail.message || detail.body || "",
          createdAt: detail.createdAt || new Date().toISOString(),
          isRead: false,
          isSpecial: true,
          isLocal: true,
        };
        setAlerts((prev) => {
          const withoutDup = prev.filter(
            (a) =>
              !(
                a.isLocal &&
                a.title === localItem.title &&
                a.message === localItem.message
              )
          );
          return [localItem, ...withoutDup];
        });
      }

      const response = await api("get", "/emailNotification/byUser");
      const apiItems = (response?.data || []).filter(isSpecialAlert);

      setAlerts((prev) => {
        const localItems = prev.filter((item) => item.isLocal);
        const apiIds = new Set(apiItems.map((item) => item._id));
        const stillLocal = localItems.filter((item) => !apiIds.has(item._id));
        return [...stillLocal, ...apiItems];
      });
    };

    fetchAlerts();
    window.addEventListener("fcm-notification", fetchAlerts);
    window.addEventListener("fcm-alert", fetchAlerts);
    return () => {
      window.removeEventListener("fcm-notification", fetchAlerts);
      window.removeEventListener("fcm-alert", fetchAlerts);
    };
  }, [isOpen, user, isMember]);

  const formatMessage = (message) => {
    if (!message) return "";
    const stripped = message.replace(
      /<a[^>]+href=["'](https?:\/\/[^"']+)["'][^>]*>.*?<\/a>/gi,
      "$1"
    );
    return stripped.replace(
      /\s*:?\s*(?:<br\s*\/?>)?\s*(https?:\/\/[^\s<>"]+)/gi,
      (_match, url) =>
        `: <a href="${url}" target="_blank" rel="noopener noreferrer" class="viewLink">View</a>`
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const markasRead = async (id) => {
    if (String(id).startsWith("local-")) {
      setAlerts((prev) => prev.filter((item) => item._id !== id));
      return;
    }

    const response = await api("post", `/emailNotification/${id}/markAsRead`);
    if (response?.status) {
      const alertsResponse = await api("get", "/emailNotification/byUser");
      setAlerts((prev) => {
        const localItems = prev.filter((item) => item.isLocal);
        return [
          ...localItems,
          ...(alertsResponse?.data || []).filter(isSpecialAlert),
        ];
      });
    }
  };

  const clearAllAlerts = async () => {
    if (!alerts.length || isClearing) return;

    setIsClearing(true);
    try {
      const apiAlerts = alerts.filter((item) => !item.isLocal);

      if (apiAlerts.length) {
        await Promise.all(
          apiAlerts.map((item) =>
            api("post", `/emailNotification/${item._id}/markAsRead`)
          )
        );
      }

      const alertsResponse = await api("get", "/emailNotification/byUser");
      const remaining = (alertsResponse?.data || []).filter(isSpecialAlert);
      setAlerts(
        remaining.length && remaining.every((item) => item.isRead)
          ? []
          : remaining.filter((item) => !item.isRead)
      );
    } finally {
      setIsClearing(false);
    }
  };

  // Only visible to members
  if (!isMember) return null;

  const unreadCount = alerts.filter((item) => !item.isRead).length;

  return (
    <div className="alerts_dropdown" ref={dropdownRef}>
      <button
        className={`alerts_toggle ${unreadCount > 0 ? "active" : ""} ${isOpen ? "open" : ""}`}
        onClick={toggleDropdown}
        type="button"
        aria-label="Severe alerts"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="21"
          height="21"
          viewBox="0 0 23.532 23.527"
          fill="none"
        >
          <path
            className="alertIconCircle"
            d="M10.928.025C2.298.57-2.783 10.245 1.612 17.727c4.5 7.66 15.67 7.765 20.238.125C26.767 9.63 20.424-.574 10.928.025m9.525 17.1c-4.052 6.513-13.585 6.443-17.475-.178-3.903-6.643.662-14.94 8.285-15.374 8.122-.461 13.507 8.613 9.19 15.552"
            fill="currentColor"
            fillRule="evenodd"
          />
          <g transform="translate(3 3) scale(0.73)">
            <line
              x1="12"
              y1="5"
              x2="12"
              y2="13.5"
              stroke="currentColor"
              strokeWidth="2.3"
              strokeLinecap="round"
            />
            <circle cx="12" cy="17.5" r="1.1" fill="currentColor" />
          </g>
        </svg>

        {unreadCount > 0 && (
          <span className="alerts_badge">{unreadCount}</span>
        )}
      </button>

      {isOpen && (
        <div className="alerts_container">
          <div className="alerts_header">
            <h2>Alerts</h2>
            {alerts.length > 0 && (
              <button
                type="button"
                className="alerts_dismissAll"
                onClick={clearAllAlerts}
                disabled={isClearing}
              >
                {isClearing ? "Clearing..." : "Dismiss all"}
              </button>
            )}
          </div>

          <div className="alerts_list">
            {alerts.length > 0 ? (
              alerts.map((alert) => (
                <div
                  key={alert._id}
                  className={`alerts_card ${alert.isRead ? "alerts_card--read" : ""}`}
                >
                  {!alert.isRead && (
                    <button
                      className="alerts_close"
                      onClick={() => markasRead(alert._id)}
                      type="button"
                    >
                      <img
                        src={Close}
                        width="15px"
                        height="15px"
                        alt="dismiss"
                      />
                    </button>
                  )}
                  <div className="alerts_top">
                    <div
                      className="alerts_title"
                      dangerouslySetInnerHTML={{ __html: alert.title }}
                    />
                    <p className="alerts_date">{formatDate(alert.createdAt)}</p>
                  </div>
                  {alert.message && (
                    <div
                      className="alerts_message"
                      dangerouslySetInnerHTML={{
                        __html: formatMessage(alert.message),
                      }}
                    />
                  )}
                </div>
              ))
            ) : (
              <p className="alerts_empty">No alerts at this time.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AlertsDropdown;
